'use client';
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { MATERIAL_CATEGORIES_DEFAULT, PRODUCT_CATEGORIES_DEFAULT, PACKAGING_CATEGORIES_DEFAULT } from '@/lib/appStore';

interface CategoryContextValue {
  materialCategories: string[];
  productCategories: string[];
  packagingCategories: string[];
  sortedMaterialCategories: string[];
  sortedProductCategories: string[];
  sortedPackagingCategories: string[];
  loading: boolean;
  addMaterialCategory: (name: string) => Promise<void>;
  addProductCategory: (name: string) => Promise<void>;
  addPackagingCategory: (name: string) => Promise<void>;
  renameMaterialCategory: (oldName: string, newName: string) => Promise<void>;
  renameProductCategory: (oldName: string, newName: string) => Promise<void>;
  renamePackagingCategory: (oldName: string, newName: string) => Promise<void>;
  deleteMaterialCategory: (name: string) => Promise<void>;
  deleteProductCategory: (name: string) => Promise<void>;
  deletePackagingCategory: (name: string) => Promise<void>;
}

const CategoryContext = createContext<CategoryContextValue | null>(null);

/** Sort alphabetically, but always pin "Other" to the bottom. */
function sortCategories(cats: string[]): string[] {
  const others = cats.filter((c) => c.toLowerCase() === 'other');
  const rest = cats.filter((c) => c.toLowerCase() !== 'other');
  rest.sort((a, b) => a.localeCompare(b));
  return [...rest, ...others];
}

export function CategoryProvider({ children }: { children: React.ReactNode }) {
  const { company } = useAuth();
  const supabase = createClient();
  const [materialCategories, setMaterialCategories] = useState<string[]>([]);
  const [productCategories, setProductCategories] = useState<string[]>([]);
  const [packagingCategories, setPackagingCategories] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);

  const loadCategories = useCallback(async () => {
    if (!company?.id) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('categories')
        .select('name, type')
        .eq('company_id', company.id)
        .order('name');

      if (error) throw error;

      const matCats = (data ?? []).filter((r) => r.type === 'material').map((r) => r.name);
      const prodCats = (data ?? []).filter((r) => r.type === 'product').map((r) => r.name);
      const packCats = (data ?? []).filter((r) => r.type === 'packaging').map((r) => r.name);

      const seedPromises: Promise<void>[] = [];
      if (matCats.length === 0) {
        seedPromises.push(seedDefaults(company.id, 'material', MATERIAL_CATEGORIES_DEFAULT));
      }
      if (prodCats.length === 0) {
        seedPromises.push(seedDefaults(company.id, 'product', PRODUCT_CATEGORIES_DEFAULT));
      }
      if (packCats.length === 0) {
        seedPromises.push(seedDefaults(company.id, 'packaging', PACKAGING_CATEGORIES_DEFAULT));
      }

      if (seedPromises.length > 0) {
        await Promise.all(seedPromises);
        const { data: seeded } = await supabase
          .from('categories')
          .select('name, type')
          .eq('company_id', company.id)
          .order('name');
        const seededMat = (seeded ?? []).filter((r) => r.type === 'material').map((r) => r.name);
        const seededProd = (seeded ?? []).filter((r) => r.type === 'product').map((r) => r.name);
        const seededPack = (seeded ?? []).filter((r) => r.type === 'packaging').map((r) => r.name);
        setMaterialCategories(seededMat);
        setProductCategories(seededProd);
        setPackagingCategories(seededPack);
      } else {
        setMaterialCategories(matCats);
        setProductCategories(prodCats);
        setPackagingCategories(packCats);
      }
    } catch {
      // On error keep whatever is already in state
    } finally {
      setLoading(false);
    }
  }, [company?.id]);

  const seedDefaults = async (companyId: string, type: 'material' | 'product' | 'packaging', defaults: string[]) => {
    const rows = defaults.map((name) => ({ company_id: companyId, name, type }));
    await supabase.from('categories').upsert(rows, { onConflict: 'company_id,name,type', ignoreDuplicates: true });
  };

  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  const addMaterialCategory = useCallback(async (name: string) => {
    if (!company?.id) return;
    const { error } = await supabase
      .from('categories')
      .insert({ company_id: company.id, name, type: 'material' });
    if (!error) setMaterialCategories((prev) => prev.includes(name) ? prev : [...prev, name]);
  }, [company?.id, supabase]);

  const addProductCategory = useCallback(async (name: string) => {
    if (!company?.id) return;
    const { error } = await supabase
      .from('categories')
      .insert({ company_id: company.id, name, type: 'product' });
    if (!error) setProductCategories((prev) => prev.includes(name) ? prev : [...prev, name]);
  }, [company?.id, supabase]);

  const addPackagingCategory = useCallback(async (name: string) => {
    if (!company?.id) return;
    const { error } = await supabase
      .from('categories')
      .insert({ company_id: company.id, name, type: 'packaging' });
    if (!error) setPackagingCategories((prev) => prev.includes(name) ? prev : [...prev, name]);
  }, [company?.id, supabase]);

  const renameMaterialCategory = useCallback(async (oldName: string, newName: string) => {
    if (!company?.id) return;
    const { error } = await supabase
      .from('categories')
      .update({ name: newName })
      .eq('company_id', company.id)
      .eq('name', oldName)
      .eq('type', 'material');
    if (!error) setMaterialCategories((prev) => prev.map((c) => (c === oldName ? newName : c)));
  }, [company?.id, supabase]);

  const renameProductCategory = useCallback(async (oldName: string, newName: string) => {
    if (!company?.id) return;
    const { error } = await supabase
      .from('categories')
      .update({ name: newName })
      .eq('company_id', company.id)
      .eq('name', oldName)
      .eq('type', 'product');
    if (!error) setProductCategories((prev) => prev.map((c) => (c === oldName ? newName : c)));
  }, [company?.id, supabase]);

  const renamePackagingCategory = useCallback(async (oldName: string, newName: string) => {
    if (!company?.id) return;
    const { error } = await supabase
      .from('categories')
      .update({ name: newName })
      .eq('company_id', company.id)
      .eq('name', oldName)
      .eq('type', 'packaging');
    if (!error) setPackagingCategories((prev) => prev.map((c) => (c === oldName ? newName : c)));
  }, [company?.id, supabase]);

  const deleteMaterialCategory = useCallback(async (name: string) => {
    if (!company?.id) return;
    const { error } = await supabase
      .from('categories')
      .delete()
      .eq('company_id', company.id)
      .eq('name', name)
      .eq('type', 'material');
    if (!error) setMaterialCategories((prev) => prev.filter((c) => c !== name));
  }, [company?.id, supabase]);

  const deleteProductCategory = useCallback(async (name: string) => {
    if (!company?.id) return;
    const { error } = await supabase
      .from('categories')
      .delete()
      .eq('company_id', company.id)
      .eq('name', name)
      .eq('type', 'product');
    if (!error) setProductCategories((prev) => prev.filter((c) => c !== name));
  }, [company?.id, supabase]);

  const deletePackagingCategory = useCallback(async (name: string) => {
    if (!company?.id) return;
    const { error } = await supabase
      .from('categories')
      .delete()
      .eq('company_id', company.id)
      .eq('name', name)
      .eq('type', 'packaging');
    if (!error) setPackagingCategories((prev) => prev.filter((c) => c !== name));
  }, [company?.id, supabase]);

  return (
    <CategoryContext.Provider
      value={{
        materialCategories,
        productCategories,
        packagingCategories,
        sortedMaterialCategories: sortCategories(materialCategories),
        sortedProductCategories: sortCategories(productCategories),
        sortedPackagingCategories: sortCategories(packagingCategories),
        loading,
        addMaterialCategory,
        addProductCategory,
        addPackagingCategory,
        renameMaterialCategory,
        renameProductCategory,
        renamePackagingCategory,
        deleteMaterialCategory,
        deleteProductCategory,
        deletePackagingCategory,
      }}
    >
      {children}
    </CategoryContext.Provider>
  );
}

export function useCategories(): CategoryContextValue {
  const ctx = useContext(CategoryContext);
  if (!ctx) throw new Error('useCategories must be used within CategoryProvider');
  return ctx;
}
